// 全局状态仓：房间快照 + 对局状态 + 事件流水。
//
// 注意事项：状态只由服务端推送驱动（state 快照直接覆盖，events 按 seq 去重追加），
// 前端不做规则推断；身份放 sessionStorage，这样同一个浏览器开多个标签页可以各自扮演一名玩家。

import { api, ApiError, TableSocket } from "./api.ts";
import type { ConnectionStatus, RoomStateData } from "./api.ts";
import { eventText } from "./format.ts";
import type {
  GameEvent,
  LegalAction,
  Pending,
  Private,
  Room,
  ServerMessage,
  Session,
  State,
} from "./types.ts";

export interface LogEntry {
  key: string;
  seq: number;
  hidden: boolean;
  text: string;
  at: number;
}

export interface PresentationFrame {
  id: number;
  before: State | null;
  beforePending: Pending | null;
  state: State;
  priv: Private | null;
  pending: Pending | null;
  seq: number;
  events: GameEvent[];
}

export interface TableSnapshot {
  me: Session | null;
  roomId: string | null;
  room: Room | null;
  state: State | null;
  priv: Private | null;
  pending: Pending | null;
  seq: number;
  log: LogEntry[];
  /** 未播完的原子动作快照；按回合顺序消费，不丢弃尚未展示的帧。 */
  frames: PresentationFrame[];
  /** Core 下发的合法动作（原样回发）；空数组＝只能等待。 */
  legal: LegalAction[];
  /** 正在处理 pending 询问的玩家（无则空串）。 */
  waitingFor: string;
  /** 卡牌 id → 卡面文字（从事件与私有信息里攒出来，用于装备/卡牌的显示名）。 */
  cards: Record<string, string>;
  status: ConnectionStatus;
  error: string | null;
}

type Listener = () => void;

const SESSION_KEY = "touranki.session";
const ROOM_KEY = "touranki.room";
const LOG_LIMIT = 240;
const SEEN_LIMIT = 800;

function readJson<T>(storage: Storage, key: string): T | null {
  const raw = storage.getItem(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    storage.removeItem(key);
    return null;
  }
}

class TableStore {
  private snapshot: TableSnapshot = {
    me: null,
    roomId: null,
    room: null,
    state: null,
    priv: null,
    pending: null,
    seq: 0,
    log: [],
    frames: [],
    legal: [],
    waitingFor: "",
    cards: {},
    status: "idle",
    error: null,
  };

  private readonly listeners = new Set<Listener>();
  private readonly socket: TableSocket;
  private readonly seen: string[] = [];
  private readonly seenSet = new Set<string>();
  private toastTimer: number | undefined;
  private roomVersion = 0;
  private frameId = 0;
  /** REST may lead the socket; its seq must not consume unseen live events. */
  private liveState: { seq: number; state: State; pending: Pending | null } | null = null;

  constructor() {
    this.snapshot.me = readJson<Session>(sessionStorage, SESSION_KEY);
    this.snapshot.roomId = readJson<string>(sessionStorage, ROOM_KEY);
    this.socket = new TableSocket({
      onMessage: (message) => this.applyMessage(message),
      onStatus: (status) => this.patch({ status }),
      onUnauthorized: () => {
        this.signOut();
        this.notify("会话已失效，请重新入席", "SESSION_EXPIRED");
      },
    });
  }

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): TableSnapshot => this.snapshot;

  finishFrame(id: number): void {
    if (this.snapshot.frames[0]?.id === id) this.patch({ frames: this.snapshot.frames.slice(1) });
  }

  /** connect 用已有会话打开实时通道（重复调用会先关掉旧连接）。 */
  connect(): void {
    const session = this.snapshot.me;
    if (!session) return;
    this.socket.close();
    this.socket.connect(session.token, this.snapshot.seq);
  }

  async createSession(name: string): Promise<void> {
    const session = await api.createSession(name);
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    this.patch({
      me: session,
      roomId: null,
      room: null,
      state: null,
      priv: null,
      pending: null,
      log: [],
      legal: [],
      waitingFor: "",
      cards: {},
      frames: [],
    });
    this.connect();
  }

  signOut(): void {
    this.socket.close();
    ++this.roomVersion;
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(ROOM_KEY);
    this.patch({
      me: null,
      roomId: null,
      room: null,
      state: null,
      priv: null,
      pending: null,
      legal: [],
      waitingFor: "",
      cards: {},
      seq: 0,
      log: [],
      frames: [],
      status: "idle",
    });
  }

  async createRoom(options: {
    name: string;
    maxPlayers: number;
    isPublic: boolean;
    mode: string;
    abilities: boolean;
    seed: number | null;
  }): Promise<void> {
    const session = this.requireSession();
    const room = await api.createRoom(session.token, {
      name: options.name,
      max_players: options.maxPlayers,
      is_public: options.isPublic,
      ruleset: {
        mode: options.mode,
        abilities: options.abilities,
        reveal_condition: false,
        short_time: false,
        no_deck_refill: false,
        hime_kome_adjust: false,
      },
      seed: options.seed,
    });
    this.rememberRoom(room.room_id);
  }

  async joinRoom(roomId: string): Promise<void> {
    const session = this.requireSession();
    const room = await api.joinRoom(session.token, roomId.trim());
    this.rememberRoom(room.room_id);
  }

  async enterRoom(roomId: string): Promise<void> {
    const session = this.requireSession();
    await api.getRoom(session.token, roomId);
    this.rememberRoom(roomId);
  }

  async leaveRoom(): Promise<void> {
    const session = this.snapshot.me;
    const roomId = this.snapshot.roomId;
    if (!session || !roomId) return;
    try {
      await api.leave(session.token, roomId);
    } catch (error) {
      this.notify("离席失败", error instanceof ApiError ? error.code : "ERROR");
      return;
    }
    if (this.snapshot.roomId !== roomId) return;
    ++this.roomVersion;
    sessionStorage.removeItem(ROOM_KEY);
    this.seen.length = 0;
    this.seenSet.clear();
    this.patch({
      roomId: null,
      room: null,
      state: null,
      priv: null,
      pending: null,
      legal: [],
      waitingFor: "",
      log: [],
      frames: [],
      cards: {},
      seq: 0,
    });
    this.connect();
  }

  async setReady(ready: boolean): Promise<void> {
    const session = this.requireSession();
    const roomId = this.requireRoomId();
    const room = await api.setReady(session.token, roomId, ready);
    this.patch({ room });
  }

  async setBots(count: number): Promise<void> {
    const session = this.requireSession();
    const roomId = this.requireRoomId();
    const room = await api.setBots(session.token, roomId, count);
    this.patch({ room });
  }

  async start(): Promise<void> {
    const session = this.requireSession();
    const room = await api.start(session.token, this.requireRoomId());
    this.patch({ room });
  }

  /** send 把一条 action 发到实时通道；失败不抛出，只弹提示。 */
  send(action: Record<string, unknown>): void {
    try {
      this.socket.send(action);
    } catch (error) {
      this.notify(
        "动作未能发出",
        error instanceof ApiError ? error.code : "ERROR",
        error instanceof ApiError ? error.message : undefined,
      );
    }
  }

  clearError(): void {
    this.patch({ error: null });
  }

  /** collectCards 把事件与询问里出现的卡面文本记下来（装备显示名要用）。 */
  private collectCards(events: GameEvent[], pending: Pending | null): void {
    const found = new Map<string, string>();
    const absorb = (card: unknown): void => {
      if (typeof card !== "object" || card === null) return;
      const { id, text } = card as { id?: unknown; text?: unknown };
      if (typeof id === "string" && typeof text === "string") found.set(id, text);
    };
    for (const event of events) absorb((event.data ?? {})["card"]);
    absorb(pending?.private_info?.card);

    const cards = { ...this.snapshot.cards };
    let changed = false;
    for (const [id, text] of found) {
      if (cards[id] !== text) {
        cards[id] = text;
        changed = true;
      }
    }
    if (changed) this.patch({ cards });
  }

  fail(title: string, error: unknown): void {
    if (error instanceof ApiError) {
      this.notify(title, error.code, error.message);
      return;
    }
    this.notify(title, "ERROR", error instanceof Error ? error.message : undefined);
  }

  private rememberRoom(roomId: string): void {
    this.seen.length = 0;
    this.seenSet.clear();
    ++this.roomVersion;
    sessionStorage.setItem(ROOM_KEY, JSON.stringify(roomId));
    this.patch({
      roomId,
      room: null,
      seq: 0,
      legal: [],
      waitingFor: "",
      log: [],
      state: null,
      priv: null,
      pending: null,
      cards: {},
      frames: [],
    });
    this.connect();
    void this.refreshRoomState();
  }

  private async refreshRoomState(): Promise<void> {
    const session = this.snapshot.me;
    const roomId = this.snapshot.roomId;
    const version = this.roomVersion;
    if (!session || !roomId) return;
    try {
      // since_seq=0：进房/刷新后补齐本人可见的历史事件。
      const data = await api.fetchState(session.token, roomId, 0);
      // REST 可落后于已经到达的 WS 终局；同一 Core seq 的认输也不能被旧 running 快照覆盖。
      if (version !== this.roomVersion || this.snapshot.roomId !== roomId || data.room.room_id !== roomId) return;
      this.applySnapshot(data);
    } catch (error) {
      if (version !== this.roomVersion || this.snapshot.roomId !== roomId) return;
      if (error instanceof ApiError && (error.code === "NOT_IN_ROOM" || error.code === "FORBIDDEN")) {
        ++this.roomVersion;
        sessionStorage.removeItem(ROOM_KEY);
        this.patch({
          roomId: null,
          room: null,
          state: null,
          priv: null,
          pending: null,
          legal: [],
          waitingFor: "",
          seq: 0,
          log: [],
          frames: [],
          cards: {},
        });
        return;
      }
      this.notify("拉取对局状态失败", error instanceof ApiError ? error.code : "ERROR");
    }
  }

  private applySnapshot(data: RoomStateData): void {
    const current = this.snapshot;
    const terminal = current.room?.phase === "finished" || current.state?.phase === "finished";
    if (data.seq < current.seq || (terminal && data.room.phase !== "finished")) return;
    if (!this.liveState && data.state) {
      this.liveState = { seq: data.seq, state: data.state, pending: data.pending ?? null };
    }
    this.collectCards(data.events ?? [], data.pending ?? null);
    this.patch({
      room: data.room,
      state: data.state ?? this.snapshot.state,
      seq: Math.max(this.snapshot.seq, data.seq),
      legal: data.legal_actions ?? [],
      waitingFor: data.waiting_for ?? "",
      ...(data.private === undefined ? {} : { priv: data.private }),
      ...(data.pending === undefined ? {} : { pending: data.pending }),
    });
    if (data.events) this.appendEvents(data.events);
  }

  private applyMessage(message: ServerMessage): void {
    switch (message.type) {
      case "welcome":
        // 握手后主动拉一次全量（带 since_seq=0），把进房/刷新后缺失的历史事件补齐；
        // 重连补全快照与历史日志不进入实时演出队列。
        void this.refreshRoomState();
        break;
      case "room":
        if (message.room.room_id !== this.snapshot.roomId) break;
        this.patch({ room: message.room });
        if (message.room.phase === "finished") void this.refreshRoomState();
        break;
      case "state": {
        if (!this.snapshot.roomId || (this.snapshot.room && this.snapshot.room.room_id !== this.snapshot.roomId)) break;
        const before = this.liveState;
        if (message.seq < (before?.seq ?? 0)) break;
        const preserveRevealed = (
          (this.snapshot.room?.phase === "finished" || this.snapshot.state?.phase === "finished") &&
          this.snapshot.state?.players.every((player) => player.role !== null) &&
          message.state.players.some((player) => player.role === null)
        );
        this.collectCards(message.events ?? [], message.pending);
        const events = (message.events ?? []).filter((event) => event.seq > (before?.seq ?? 0));
        const frame: PresentationFrame = {
          id: ++this.frameId,
          before: before?.state ?? this.snapshot.state,
          beforePending: before ? before.pending : this.snapshot.pending,
          state: message.state,
          priv: message.private,
          pending: message.pending,
          seq: message.seq,
          events,
        };
        this.liveState = { seq: message.seq, state: message.state, pending: message.pending };
        this.patch({
          ...(message.seq >= this.snapshot.seq && !preserveRevealed ? {
            seq: frame.seq,
            state: frame.state,
            priv: frame.priv,
            pending: frame.pending,
            legal: message.legal_actions ?? [],
            waitingFor: message.waiting_for ?? "",
          } : {}),
          frames: events.length > 0
            ? [...this.snapshot.frames, frame]
            : this.snapshot.frames,
        });
        if (message.events) this.appendEvents(message.events);
        break;
      }
      case "events":
        if (this.snapshot.roomId) this.appendEvents(message.events ?? []);
        break;
      case "response":
        if (!message.ok && message.error) {
          this.notify("动作被拒绝", message.error.code, message.error.message);
        }
        break;
      case "error":
        this.notify("服务端错误", message.code, message.message);
        break;
      default:
        break;
    }
  }

  private appendEvents(events: GameEvent[]): void {
    const room = this.snapshot.room;
    const state = this.snapshot.state;
    this.collectCards(events, null);
    const fresh: LogEntry[] = [];
    for (const event of events) {
      const key = `${this.snapshot.roomId ?? ""}|${event.seq}|${event.type}|${JSON.stringify(event.data ?? {})}`;
      if (this.seenSet.has(key)) continue;
      this.seenSet.add(key);
      this.seen.push(key);
      if (this.seen.length > SEEN_LIMIT) {
        const dropped = this.seen.splice(0, this.seen.length - SEEN_LIMIT);
        for (const old of dropped) this.seenSet.delete(old);
      }
      fresh.push({
        key,
        seq: event.seq,
        hidden: event.visibility !== "public",
        text: eventText(event, { state, room, cards: this.snapshot.cards }),
        at: Date.now(),
      });
    }
    if (fresh.length > 0) this.patch({ log: [...this.snapshot.log, ...fresh].slice(-LOG_LIMIT) });
  }

  private requireSession(): Session {
    const session = this.snapshot.me;
    if (!session) throw new ApiError("NO_SESSION", "尚未入席");
    return session;
  }

  private requireRoomId(): string {
    const roomId = this.snapshot.roomId;
    if (!roomId) throw new ApiError("NO_ROOM", "尚未进入房间");
    return roomId;
  }

  private notify(title: string, code: string, detail?: string): void {
    window.clearTimeout(this.toastTimer);
    this.patch({ error: `${title}［${code}］${detail ? ` ${detail}` : ""}` });
    this.toastTimer = window.setTimeout(() => this.patch({ error: null }), 6000);
  }

  private patch(partial: Partial<TableSnapshot>): void {
    if (partial.state === null || (partial.roomId !== undefined && partial.roomId !== this.snapshot.roomId)) {
      this.liveState = null;
    }
    this.snapshot = { ...this.snapshot, ...partial };
    for (const listener of this.listeners) listener();
  }
}

export const table = new TableStore();
